import { ErrorResponse } from './../../../models/error.model';
import { UtilsService } from 'src/app/services/utils.service';
import { SelectOptionsService } from './../../../services/select-options.service';
import { ControlValueAccessor, UntypedFormGroup, UntypedFormControl, Validators, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, OnInit, Input, forwardRef } from '@angular/core';
import { Locality, Municipality, Province } from 'src/app/models/locality.model';
import { ID } from '../../../models/ID.model';

@Component({
    selector: 'app-locality-selector',
    templateUrl: './locality-selector.component.html',
    styleUrls: ['./locality-selector.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LocalitySelectorComponent), multi: true }
    ]
})
export class LocalitySelectorComponent implements OnInit, ControlValueAccessor {

    @Input()
    provinces?: Province[];

    municipalities?: Municipality[];
    localities?: Locality[];

    localitySelectorFG = new UntypedFormGroup({
        province: new UntypedFormControl('', [Validators.required]),
        municipality: new UntypedFormControl('', [Validators.required]),
        locality: new UntypedFormControl('', [Validators.required]),
    });

    disabled = false;

    onChange: (p: Locality | null) => void = () => {};
    onTouched: () => void = () => {};

    @Input()
    get value(): Locality | null {
        if (this.localitySelectorFG.controls.locality.value) {
            this.getLocalityFromCode(this.localitySelectorFG.controls.locality.value);
        }
        return null;
    }
    set value(locality: Locality | null) {
        this.localitySelectorFG.controls.locality.setValue(locality?.codigo);
        this.onChange(locality);
    }

    constructor(
        private selectOptionsService: SelectOptionsService,
        private utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        this.updateSelects();
    }

    updateSelects() {
        if (this.disabled) {
            this.localitySelectorFG.disable();
            return;
        }
        if (!this.provinces) {
            this.localitySelectorFG.controls.province.disable();
            this.getProvinces();
        }
        this.localitySelectorFG.controls.municipality.disable();
        this.municipalities = undefined;
        if (this.localitySelectorFG.controls.province.value)
            this.getMunicipalities(this.localitySelectorFG.controls.province.value);
        this.localitySelectorFG.controls.locality.disable();
        this.localities = undefined;
        if (this.localitySelectorFG.controls.province.value && this.localitySelectorFG.controls.municipality.value)
            this.getLocalities(this.localitySelectorFG.controls.province.value, this.localitySelectorFG.controls.municipality.value);
    }

    getProvinces() {
        this.selectOptionsService.getProvinces().subscribe(
            provinces => {
                this.provinces = provinces;
                if (!this.disabled)
                    this.localitySelectorFG.controls.province.enable();
            },
            (error: ErrorResponse) => this.utilsService.alert('Error al obtener lista de provincias', error.error?.descripcion)
        );
    }

    getMunicipalities(provinceId: ID) {
        this.selectOptionsService.getMunicipalities(provinceId).subscribe(
            municipalities => {
                this.municipalities = municipalities;
                if (!this.disabled)
                    this.localitySelectorFG.controls.municipality.enable();
            },
            (error: ErrorResponse) => this.utilsService.alert('Error al obtener lista de municipios', error.error?.descripcion)
        );
    }

    getLocalities(provinceId: ID, municipalityId: ID) {
        this.selectOptionsService.getLocalities(provinceId, municipalityId).subscribe(
            localities => {
                this.localities = localities;
                if (!this.disabled)
                    this.localitySelectorFG.controls.locality.enable();
            },
            (error: ErrorResponse) => this.utilsService.alert('Error al obtener lista de localidades', error.error?.descripcion)
        );
    }

    writeValue(obj: any): void {
        if (obj !== null && obj !== undefined && obj.codigo && obj.descripcion && obj.municipio && obj.provincia) {
            this.localitySelectorFG.setValue({
                province: obj.provincia.codigo,
                municipality: obj.municipio.codigo,
                locality: obj.codigo
            });
            this.updateSelects();
            this.onChange(obj);
        } else {
            this.localitySelectorFG.setValue({
                province: null,
                municipality: null,
                locality: null
            });
            this.updateSelects();
            this.onChange(obj);
        }
    }

    registerOnChange(fn: (p: Locality | null) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.updateSelects();
    }

    updateProvince(newValue: string | null) {
        this.localitySelectorFG.controls.municipality.setValue(null);
        this.localitySelectorFG.controls.municipality.disable();
        if (newValue) {
            this.getMunicipalities(newValue);
        }
    }

    updateMunicipality(newValue: string | null) {
        this.localitySelectorFG.controls.locality.setValue(null);
        this.localitySelectorFG.controls.locality.disable();
        if (newValue) {
            this.getLocalities(this.localitySelectorFG.controls.province.value, newValue);
        }
    }

    updateLocality(newValue: string | null) {
        this.onChange(this.getLocalityFromCode(newValue));
    }

    getLocalityFromCode(code: string | null) {
        const val = this.localities?.find(locality => locality.codigo === code);
        if (val && !val.municipio) {
            val.municipio = this.municipalities?.find(municipality => municipality.codigo === this.localitySelectorFG.value.municipality);
        }
        if (val && !val.provincia) {
            val.provincia = this.provinces?.find(province => province.codigo === this.localitySelectorFG.value.province);
        }
        return val;
    }
}
